/**
 * EquipeForm — modale création/édition d'une équipe de service.
 */
import { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import { colors } from "../styles/tokens";
import useMembers from "../hooks/useMembers";

const COULEURS = [
  "#E4A72C", // or
  "#A78BFA", // violet
  "#60A5FA", // bleu
  "#2DD4A0", // vert
  "#FB923C", // orange
  "#E05B6A", // rose
  "#34D399", // émeraude
  "#F472B6", // pink
];

const EMPTY = { nom: "", couleur: "#E4A72C", eglise_id: "", responsable_id: "" };

export default function EquipeForm({ open, onClose, onSubmit, onDelete, eglises = [], initial = null }) {
  const { members } = useMembers();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nom: initial.nom || "",
        couleur: initial.couleur || "#E4A72C",
        eglise_id: initial.eglise_id ? String(initial.eglise_id) : "",
        responsable_id: initial.responsable_id ? String(initial.responsable_id) : "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    if (key === "eglise_id") {
      setForm((p) => ({ ...p, eglise_id: value, responsable_id: "" }));
    } else {
      setForm((p) => ({ ...p, [key]: value }));
    }
    setErrors((p) => ({ ...p, [key]: null }));
  };

  const responsableOptions = useMemo(() => {
    if (!form.eglise_id) return [];
    return members
      .filter((m) => m.eglise_id === Number(form.eglise_id))
      .map((m) => ({
        value: String(m.id),
        label: `${m.prenom} ${m.nom}${m.role_eglise ? ` — ${m.role_eglise}` : ""}`,
      }));
  }, [members, form.eglise_id]);

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = "Le nom est obligatoire";
    if (!form.eglise_id) e.eglise_id = "Veuillez sélectionner une église";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setGlobalError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        couleur: form.couleur,
        eglise_id: Number(form.eglise_id),
        responsable_id: form.responsable_id ? Number(form.responsable_id) : null,
      };
      const result = await onSubmit(payload);
      if (result?.ok === false) setGlobalError(result.error || "Erreur");
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!initial || !onDelete) return;
    if (!window.confirm(`Désactiver l'équipe « ${initial.nom} » ?`)) return;
    setSubmitting(true);
    try {
      await onDelete(initial);
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((e) => ({
    value: String(e.id),
    label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier l'équipe" : "Nouvelle équipe de service"}
      subtitle={initial ? `« ${initial.nom} »` : "Louange, Son, Accueil, Sécurité, Enfants…"}
      size="md"
      footer={
        <>
          {initial && onDelete && (
            <Button variant="danger" onClick={handleDelete} disabled={submitting} style={{ marginRight: "auto" }}>
              🗑 Désactiver
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Sauvegarder" : "Créer l'équipe"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        <Input
          label="Nom de l'équipe"
          value={form.nom}
          onChange={set("nom")}
          placeholder="Louange, Son, Accueil…"
          required
          error={errors.nom}
        />

        {/* Sélecteur de couleur */}
        <div>
          <div style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted, marginBottom: "8px" }}>
            Couleur
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {COULEURS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => set("couleur")(c)}
                title={c}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: c,
                  border: form.couleur === c
                    ? `3px solid ${colors.text}`
                    : `2px solid ${colors.border}`,
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </div>

        <Select
          label="Église"
          value={form.eglise_id}
          onChange={set("eglise_id")}
          options={eglisesOptions}
          required
          error={errors.eglise_id}
          placeholder="Sélectionnez une église..."
        />

        <Select
          label="Responsable (optionnel)"
          value={form.responsable_id}
          onChange={set("responsable_id")}
          options={responsableOptions}
          placeholder={
            !form.eglise_id ? "Sélectionnez d'abord une église"
            : responsableOptions.length === 0 ? "Aucun membre dans cette église"
            : "Aucun"
          }
          disabled={!form.eglise_id || responsableOptions.length === 0}
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
